/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * NotificationBell - Header bell icon with unread badge and dropdown.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import notificationService from '../services/notificationService'
import webSocketService from '../services/webSocketService'

const NotificationBell = () => {
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef(null)

  // Fetch unread count on mount
  useEffect(() => {
    notificationService
      .getUnreadCount()
      .then(setUnreadCount)
      .catch(() => {})
  }, [])

  // Listen for real-time notifications
  useEffect(() => {
    const socket = webSocketService.getSocket?.()
    if (!socket) return

    const handler = (data) => {
      setUnreadCount((c) => c + 1)
      setNotifications((prev) => [data, ...prev].slice(0, 20))
    }
    socket.on('notification:new', handler)
    return () => socket.off('notification:new', handler)
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const handleOpen = useCallback(async () => {
    setOpen((o) => !o)
    if (!open) {
      setLoading(true)
      try {
        const res = await notificationService.list({ limit: 10 })
        setNotifications(res.notifications || [])
        setUnreadCount(res.unreadCount || 0)
      } catch {
        // ignore
      } finally {
        setLoading(false)
      }
    }
  }, [open])

  const handleClick = useCallback(
    async (notif) => {
      setOpen(false)
      if (!notif.isRead) {
        await notificationService.markRead(notif.id).catch(() => {})
        setUnreadCount((c) => Math.max(0, c - 1))
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, isRead: true } : n))
        )
      }
      if (notif.link) navigate(notif.link)
    },
    [navigate]
  )

  const handleMarkAllRead = useCallback(async () => {
    await notificationService.markAllRead().catch(() => {})
    setUnreadCount(0)
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
  }, [])

  const formatTime = (dateStr) => {
    const d = new Date(dateStr)
    const now = new Date()
    const diffMs = now - d
    const mins = Math.floor(diffMs / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-72">
            {loading && (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No notifications yet
              </div>
            )}
            {!loading &&
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    !n.isRead ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.isRead && (
                      <span className="w-2 h-2 mt-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                    )}
                    <div className={!n.isRead ? '' : 'ml-4'}>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {n.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                        {n.message}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {formatTime(n.createdAt)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
